import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useRecoilValue } from 'recoil';
import {
  Box,
  Typography,
  Card,
  Switch,
  TextField,
  Select,
  MenuItem,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  useMediaQuery,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  FileText,
  Car,
  ClipboardCheck,
  Book,
  Info,
  Image as ImageIcon,
  SlidersHorizontal,
  Package,
  Palette,
  Type as TypeIcon,
  ShoppingCart,
  Save,
  AlertTriangle,
  Pencil,
  Upload,
  KeyRound,
  LogOut,
  CheckCircle2,
  MessageCircle,
  UserPlus,
  Printer,
  Bell,
  XCircle,
  Trash2,
} from 'lucide-react';
import PageScaffold from '../component/pageScaffold/PageScaffold';
import TextAreaWithCounter from '../component/input/TextAreaWithCounter';
import DialogForm from '../component/dialog/DialogForm';
import { httpClient } from '../common/httpclient';
import { useEnterpriseConfigContext } from '../component/context/EnterpriseConfigContext';
import { sidebarExpandedSelector } from '../component/layout/sidebarState';
import { usePermissions } from '../hooks/usePermissions';
import { useIsMobileShell } from '../hooks/useIsMobileShell';
import { useAppBarSubtitle } from '../component/layout/AppBarActions';
import WhatsappConnection from './whatsapp';

// DESIGN-SYSTEM.md §6.4 "Settings page" / prototipo-configuracoes.html
// (view-sistema) — cópia local do `ff-textarea` (mesmo padrão de
// `pages/service_order/orderData.js` `ffFieldSx`) usado nos textareas dos
// modais de Banner/Termos.
const ffFieldSx = {
  '& .MuiInputBase-input': { fontSize: '13.5px' },
  // Label do `.ff-label`: repouso 13.5px, encolhido 10px/800 uppercase dentro
  // da caixa (o `legend` é removido abaixo, então não pode cruzar a borda).
  '& .MuiInputLabel-root': {
    fontSize: '13.5px',
    color: 'text.disabled',
    transform: 'translate(14px, 17px) scale(1)',
  },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    fontSize: '10px',
    fontWeight: 800,
    color: 'primary.main',
    letterSpacing: '.04em',
    textTransform: 'uppercase',
    transform: 'translate(14px, 8px) scale(1)',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'background.subtle',
    '&.MuiInputBase-multiline': {
      height: 'auto',
      minHeight: '140px',
      alignItems: 'flex-start',
      // `.ff-textarea{padding-top:24px}` — reserva p/ o label encolhido.
      padding: '24px 14px 7px 14px',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderWidth: '1.5px',
    borderColor: 'divider',
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    display: 'none',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'primary.main',
    borderWidth: '1.5px',
  },
  '& .MuiInputBase-multiline textarea': {
    resize: 'vertical',
  },
};

// Botões da barra de salvar no mobile (`.formbar .btn`): 48px, raio 14, 14.5/700, largura igual.
const mobileBarButtonSx = { flex: 1, height: 48, borderRadius: '14px', fontSize: '14.5px', fontWeight: 700, lineHeight: 1.4, padding: '0 18px' };

// `sr-num`/`sr-select` — controle compacto dentro de um `setting-row`
// (altura 36px, radius 8, fundo `background.subtle`), diferente do
// `.ff-input` (52px) usado nos formulários de inserção.
const compactNumberSx = {
  // Largura folgada (não os 90px "sr-num" do protótipo) para caber valores
  // reais de vários dígitos sem cortar/rolar o texto (dado real observado em
  // QA: capacidade de estoque configurada com 10 dígitos).
  width: 132,
  '& .MuiOutlinedInput-root': {
    height: '36px',
    borderRadius: '8px',
    backgroundColor: 'background.subtle',
  },
  '& .MuiOutlinedInput-input': {
    textAlign: 'right',
    padding: '0 10px',
    fontSize: '13px',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'primary.main',
  },
};

const compactSelectSx = {
  minWidth: 128,
  height: '36px',
  borderRadius: '8px',
  backgroundColor: 'background.subtle',
  fontSize: '13px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
  '& .MuiSelect-select': {
    padding: '0 32px 0 10px',
    display: 'flex',
    alignItems: 'center',
    height: '36px !important',
  },
};

const iconBtnSx = {
  width: 34,
  height: 34,
  borderRadius: '9px',
  border: '1px solid',
  borderColor: 'divider',
  color: 'text.secondary',
  '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
};

// `set-card-head` icon chip gradients (§4/§9) — mesmas cores fixas do
// protótipo (`.ic.blue/.purple/.green`), iguais em claro/escuro.
const CARD_GRADIENTS = {
  blue: (theme) => theme.palette.gradient.brand,
  purple: 'linear-gradient(135deg, #a78bfa, #8b5cf6)',
  green: 'linear-gradient(135deg, #4ade80, #16a34a)',
};

function SettingsCard({ icon: Icon, tone, title, subtitle, children }) {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          p: '18px 20px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '11px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            background: (t) =>
              typeof CARD_GRADIENTS[tone] === 'function' ? CARD_GRADIENTS[tone](t) : CARD_GRADIENTS[tone],
          }}
        >
          <Icon size={18} strokeWidth={2} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{title}</Typography>
          <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
        </Box>
      </Box>
      <Box sx={{ p: '6px 20px 18px', flex: 1 }}>{children}</Box>
    </Card>
  );
}

function SettingRow({ icon: Icon, iconBg, iconColor, title, description, control, isLast, alignStart, children }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: alignStart ? 'flex-start' : 'center',
        gap: '14px',
        py: '14px',
        borderBottom: isLast ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: '10px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: iconBg || 'background.subtle',
          color: iconColor || 'text.secondary',
        }}
      >
        <Icon size={16} strokeWidth={2} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: '1px' }}>{description}</Typography>
        {children}
      </Box>
      {control && (
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>{control}</Box>
      )}
    </Box>
  );
}

function ConnBadge({ connected }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '11px',
        fontWeight: 700,
        padding: '5px 10px',
        borderRadius: '20px',
        backgroundColor: connected ? 'success.bg' : 'background.subtle',
        color: connected ? 'success.main' : 'text.disabled',
        whiteSpace: 'nowrap',
      }}
    >
      ● {connected ? 'Conectado' : 'Desconectado'}
    </Box>
  );
}

function TextPreview({ value }) {
  return (
    <Box
      sx={{
        mt: 2.5,
        p: '14px',
        borderRadius: '12px',
        backgroundColor: 'background.subtle',
        border: '1px dashed',
        borderColor: 'divider',
      }}
    >
      <Typography
        sx={{
          fontSize: '10.5px',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '.05em',
          color: 'text.disabled',
          mb: 1,
        }}
      >
        Pré-visualização
      </Typography>
      <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

export default function Settings() {
  const theme = useTheme();
  const { refetch: refetchEnterpriseConfig } = useEnterpriseConfigContext();
  const { hasModule, hasPermission, hasAnyPermission } = usePermissions();
  const [queryParams, setQueryParams] = useSearchParams();

  // Abas das sessões. Todo o estado (settings, isDirty, savebar) continua no
  // componente pai, então trocar de aba não perde alterações não salvas.
  const hasMarketplace = hasModule('marketplace');
  const hasWhatsapp = hasModule('whatsapp');
  // Permissões por sessão (RBAC): `view` mostra a aba (somente leitura), `update` permite alterar.
  const TAB_PERMISSIONS = {
    os: { view: 'settings.serviceorder.view', update: 'settings.serviceorder.update' },
    sales: { view: 'settings.sale.view', update: 'settings.sale.update' },
    general: { view: 'settings.general.view', update: 'settings.general.update' },
    integrations: { view: 'settings.integration.view', update: 'settings.integration.update' },
    // Preferências de aviso por WhatsApp seguem a permissão da sessão Gerais (o backend grava as chaves notify_wa_* com ela).
    notifications: { view: 'settings.general.view', update: 'settings.general.update' },
  };
  const canSeeTab = (key) => hasAnyPermission([TAB_PERMISSIONS[key].view, TAB_PERMISSIONS[key].update]);
  const canEditTab = (key) => hasPermission(TAB_PERMISSIONS[key].update);
  const settingsTabs = [
    { key: 'os', label: 'Ordem de Serviço' },
    { key: 'sales', label: 'Vendas' },
    { key: 'general', label: 'Gerais' },
    // Avisos por WhatsApp (enviados pelo número do sistema) valem para todos os planos.
    { key: 'notifications', label: 'Notificações' },
    // Aba aparece se o plano tem Marketplace ou WhatsApp (cada card tem seu próprio gate).
    ...(hasMarketplace || hasWhatsapp ? [{ key: 'integrations', label: 'Integrações' }] : []),
  ].filter((tab) => canSeeTab(tab.key));
  // `?tab=integrations` permite abrir direto na aba (ex.: antiga rota /whatsapp).
  const [activeTab, setActiveTab] = useState(
    ['integrations', 'notifications'].includes(queryParams.get('tab')) ? queryParams.get('tab') : 'os'
  );
  // Garante aba válida (módulo de integração ausente ou sem permissão na sessão).
  const currentTab = settingsTabs.some((t) => t.key === activeTab) ? activeTab : (settingsTabs[0] ? settingsTabs[0].key : 'os');
  const currentTabReadOnly = settingsTabs.length > 0 && !canEditTab(currentTab);

  // Barra fixa "alterações não salvas" (DESIGN-SYSTEM.md §6.4) — mesmo
  // deslocamento `left` do menu lateral já usado em `pages/products/Form.js`
  // (`actionFooterLeft`), para a barra nunca sobrepor o sidebar.
  const isSidebarMobile = useMediaQuery('(max-width:1023.95px)');
  const isMobileShell = useIsMobileShell();
  const isSidebarExpanded = useRecoilValue(sidebarExpandedSelector);
  const actionFooterLeft = isSidebarMobile ? 0 : (isSidebarExpanded ? 264 : 76);

  // Estado para as configurações
  const [settings, setSettings] = useState({
    // Ordem de Serviço
    os_banner: '',
    os_terms: '',
    os_use_vehicle: false,
    os_use_checklist: false,
    os_notify_customer_on_finish: false,
    os_auto_send_upcoming_services: false,
    // Gerais
    maximum_stock_size: 1000,
    theme: localStorage.getItem('theme') || 'light',
    pdv_image_path: '',
    text_uppercase: false,
    // Vendas
    sale_identify_customer: true,
    sale_print_receipt: true,
    // Notificações (avisos ao tenant por WhatsApp)
    notify_wa_low_stock: false,
    notify_wa_sale_created: false,
    notify_wa_sale_canceled: false,
    notify_wa_os_billed: false,
    notify_wa_os_canceled: false,
    notify_wa_os_deleted: false
  });

  // Snapshot do último estado persistido (carregado da API / após salvar),
  // usado pela barra de "alterações não salvas" para detectar `isDirty` e
  // para reverter no "Descartar" — mesmo papel do array `SETTINGS_DEFAULTS`
  // implícito no protótipo.
  const originalSettingsRef = useRef(settings);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [bannerModalOpen, setBannerModalOpen] = useState(false);
  const [bannerText, setBannerText] = useState('');
  const [mercadoLivreAuthLoading, setMercadoLivreAuthLoading] = useState(false);
  const [mercadoLivreDisconnectLoading, setMercadoLivreDisconnectLoading] = useState(false);
  const [mercadoLivreDisconnectDialogOpen, setMercadoLivreDisconnectDialogOpen] = useState(false);
  const mercadoLivreAuthStorageKey = 'mercado_livre_authorized';
  const successParam = queryParams.get('success');
  const isMercadoLivreAuthOk = successParam === 'ok';
  const isMercadoLivreAuthTrue = successParam === 'true';
  const isMercadoLivreAuthStored = localStorage.getItem(mercadoLivreAuthStorageKey) === 'true';
  // eslint-disable-next-line no-unused-vars
  const isMercadoLivreAuthDisabled = isMercadoLivreAuthOk || isMercadoLivreAuthStored;
  const isMercadoLivreConnected = isMercadoLivreAuthTrue || isMercadoLivreAuthStored;

  // Carregar configurações do localStorage
  useEffect(() => {
    // Primeiro, carrega o tema do localStorage (fonte de verdade)
    const storedTheme = localStorage.getItem('theme') || 'light';

    httpClient.get(`/enterprise/cfg/all`).then(response => {
      if (response.status === 200) {
        // Faz merge dos dados da API com os valores padrão para garantir que todos os campos existam
        setSettings(prev => {
          const next = {
            ...prev,
            ...response.data,
            // Prioriza o tema do localStorage (fonte de verdade), senão usa o da API, senão usa 'light'
            theme: storedTheme || response.data.theme || prev.theme || 'light'
          };
          originalSettingsRef.current = next;
          return next;
        });
      }
    });
  }, []);

  // Celular do cadastro (Minha Conta): destino dos avisos por WhatsApp. null = ainda não carregado/indisponível.
  const [enterpriseCellPhone, setEnterpriseCellPhone] = useState(null);
  useEffect(() => {
    if (currentTab !== 'notifications') return;
    httpClient.get('/enterprise/account-info')
      .then((response) => {
        if (response.status === 200 && response.data) {
          setEnterpriseCellPhone(response.data.cell_phone || response.data.phone_number || '');
        }
      })
      .catch(() => setEnterpriseCellPhone(null));
  }, [currentTab]);

  useEffect(() => {
    if (isMercadoLivreAuthOk || isMercadoLivreAuthTrue) {
      localStorage.setItem(mercadoLivreAuthStorageKey, 'true');
    }
  }, [isMercadoLivreAuthOk, isMercadoLivreAuthTrue, mercadoLivreAuthStorageKey]);

  // Listener para sincronizar o tema quando mudar no localStorage (ex: pelo menu de conta)
  useEffect(() => {
    const handleThemeChange = (event) => {
      const newTheme = event.detail?.theme || localStorage.getItem('theme') || 'light';
      setSettings(prev => ({
        ...prev,
        theme: newTheme
      }));
    };

    const handleStorageChange = () => {
      const newTheme = localStorage.getItem('theme') || 'light';
      setSettings(prev => ({
        ...prev,
        theme: newTheme
      }));
    };

    // Listener para evento personalizado de mudança de tema
    window.addEventListener('themeChanged', handleThemeChange);

    // Listener para mudanças no localStorage
    window.addEventListener('storage', handleStorageChange);

    // Polling para verificar mudanças (fallback)
    const interval = setInterval(() => {
      const storedTheme = localStorage.getItem('theme') || 'light';
      setSettings(prev => {
        if (prev.theme !== storedTheme) {
          return {
            ...prev,
            theme: storedTheme
          };
        }
        return prev;
      });
    }, 1000);

    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Salvar configurações
  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await httpClient.put(`/enterprise/config`, settings);
      if (response.status === 204) {
        // Atualizar as configurações no contexto para aplicar as mudanças imediatamente
        await refetchEnterpriseConfig();
        originalSettingsRef.current = settings;
        setIsDirty(false);
        setSnackbar({
          open: true,
          message: 'Configuração salva com sucesso!',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Erro ao salvar configuração!',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar configuração!',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Descartar alterações não salvas — volta ao último snapshot persistido
  // (DESIGN-SYSTEM.md §6.4 "Descartar/Salvar"), inclusive revertendo o tema
  // já aplicado ao vivo (localStorage + evento `themeChanged`).
  const discardChanges = () => {
    const original = originalSettingsRef.current;
    setSettings(original);
    if (original.theme && localStorage.getItem('theme') !== original.theme) {
      localStorage.setItem('theme', original.theme);
      window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme: original.theme } }));
    }
    setIsDirty(false);
    setSnackbar({ open: true, message: 'Alterações descartadas.', severity: 'info' });
  };

  // Atualizar configuração
  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setIsDirty(true);
  };

  // Funções para gerenciar o modal do banner
  const handleOpenBannerModal = () => {
    setBannerText(settings.os_banner || '');
    setBannerModalOpen(true);
  };

  const handleCloseBannerModal = () => {
    setBannerModalOpen(false);
    setBannerText('');
  };

  const handleSaveBannerText = () => {
    setSettings(prev => ({
      ...prev,
      os_banner: bannerText
    }));
    setIsDirty(true);
    setBannerModalOpen(false);
  };

  const handleMercadoLivreAuth = async () => {
    setMercadoLivreAuthLoading(true);

    try {
      const response = await httpClient.get('/marketplace/mercadolivre/auth/url');

      if (response.status === 200 && response.data) {
        const authUrl =
          response.data.url ||
          response.data.auth_url ||
          response.data.authorization_url ||
          response.data;

        if (typeof authUrl === 'string' && authUrl.length > 0) {
          window.open(authUrl, '_blank', 'noopener,noreferrer');
          setSnackbar({
            open: true,
            message: 'Link de autenticação aberto em nova aba.',
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Não foi possível obter o link de autenticação.',
            severity: 'error'
          });
        }
      } else {
        setSnackbar({
          open: true,
          message: 'Erro ao gerar o link de autenticação.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Erro ao gerar link do Mercado Livre:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao gerar o link de autenticação.',
        severity: 'error'
      });
    } finally {
      setMercadoLivreAuthLoading(false);
    }
  };

  // Abre a confirmação de desconexão (mesmo padrão de ação destrutiva do
  // `DialogForm` usado em `pages/products/List.js` para exclusão).
  const handleOpenMercadoLivreDisconnect = () => {
    setMercadoLivreDisconnectDialogOpen(true);
  };

  const handleCloseMercadoLivreDisconnect = () => {
    setMercadoLivreDisconnectDialogOpen(false);
  };

  const handleMercadoLivreDisconnect = async () => {
    setMercadoLivreDisconnectLoading(true);

    try {
      const response = await httpClient.delete('/marketplace/mercadolivre/disconnect');

      if (response.status === 204 || response.status === 200) {
        localStorage.removeItem(mercadoLivreAuthStorageKey);
        // Remove o `?success=ok|true` da URL, se ainda presente do último
        // fluxo de autenticação, para não recalcular como "conectado".
        if (queryParams.get('success')) {
          const nextParams = new URLSearchParams(queryParams);
          nextParams.delete('success');
          setQueryParams(nextParams, { replace: true });
        }
        setMercadoLivreDisconnectDialogOpen(false);
        setSnackbar({
          open: true,
          message: 'Mercado Livre desconectado com sucesso.',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Erro ao desconectar do Mercado Livre.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Erro ao desconectar do Mercado Livre:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao desconectar do Mercado Livre.',
        severity: 'error'
      });
    } finally {
      setMercadoLivreDisconnectLoading(false);
    }
  };

  // Manipular upload de arquivo
  const handleFileUpload = (key, event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async () => {
        await handleLogoUpload(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setSnackbar({ open: true, message: 'Tipo de arquivo não suportado. Use JPG, PNG ou GIF.', severity: 'error' });
      return;
    }

    // Validar tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'Arquivo muito grande. Tamanho máximo: 5MB.', severity: 'error' });
      return;
    }

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await httpClient.postFile('/enterprise/upload-logo?configName=os_logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.status === 200 || response.status === 201) {
        setSnackbar({ open: true, message: 'Logo enviado com sucesso!', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Erro no upload', severity: 'error' });
      }
    } catch (error) {
      console.error('Erro no upload do logo:', error);
      setSnackbar({ open: true, message: 'Erro ao enviar logo. Tente novamente.', severity: 'error' });
    }
  };

  // Aplicar tema
  const applyTheme = (newTheme) => {
    updateSetting('theme', newTheme);
    localStorage.setItem('theme', newTheme);
    // Disparar evento personalizado para notificar outros componentes
    window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme: newTheme } }));
  };

  useAppBarSubtitle(isMobileShell && isDirty ? 'Alterações pendentes' : null);

  const stockCapacityLabel = `Define o limite total de itens no estoque (${(settings.maximum_stock_size || 0).toLocaleString('pt-BR')} itens).`;

  return (
    <PageScaffold titlePageTarget="Configurações" links={[]}>
      <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', pb: '90px' }}>
        {/* page-head — DESIGN-SYSTEM.md §6.4 / prototipo-configuracoes.html (view-sistema) */}
        {!isMobileShell && <Box sx={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '11px',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                background: (t) => t.palette.gradient.brand,
              }}
            >
              <SettingsIcon size={18} strokeWidth={2} />
            </Box>
            <Box>
              <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                Configurações do Sistema
              </Typography>
              <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                Comportamentos e preferências que valem para toda a empresa.
              </Typography>
            </Box>
          </Box>
        </Box>}

        {isMobileShell ? (
          // Mobile: controle segmentado rolável (`.seg` do protótipo).
          <Box role="tablist" aria-label="Sessões de configurações" sx={{ display: 'flex', backgroundColor: 'background.subtle', border: '1px solid', borderColor: 'divider', borderRadius: '14px', p: '3px', gap: '2px', overflowX: 'auto', mb: '12px' }}>
            {settingsTabs.map((tab) => {
              const active = tab.key === currentTab;
              return (
                <Box
                  key={tab.key}
                  component="button"
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(tab.key)}
                  sx={{ flex: '1 0 auto', height: 36, p: '0 14px', borderRadius: '11px', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', border: 0, cursor: 'pointer', whiteSpace: 'nowrap', backgroundColor: active ? 'background.paper' : 'transparent', color: active ? 'primary.main' : 'text.secondary', boxShadow: active ? '0 1px 2px rgba(15,23,42,.06)' : 'none' }}
                >
                  {tab.label}
                </Box>
              );
            })}
          </Box>
        ) : (
        <Tabs
          value={currentTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Sessões de configurações"
          sx={{ mb: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          {settingsTabs.map((tab) => (
            <Tab key={tab.key} value={tab.key} label={tab.label} />
          ))}
        </Tabs>
        )}

        {currentTabReadOnly && (
          <Typography sx={{ mb: 1.5, fontSize: 12.5, color: 'text.secondary' }}>
            Você pode visualizar esta sessão, mas não alterar. Peça a permissão de alteração ao administrador.
          </Typography>
        )}

        {/* Uma sessão (card) por aba: Ordem de Serviço / Vendas / Gerais / Integrações.
            fieldset desabilitado = somente leitura quando o perfil só tem `view` da sessão. */}
        <Box
          component="fieldset"
          disabled={currentTabReadOnly}
          sx={{
            border: 0,
            p: 0,
            m: 0,
            minWidth: 0,
            opacity: currentTabReadOnly ? 0.75 : 1,
            width: '100%',
            ...(currentTab === 'integrations' && {
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
              alignItems: 'start',
            }),
          }}
        >
          {/* ORDEM DE SERVIÇO */}
          {currentTab === 'os' && (
          <SettingsCard icon={FileText} tone="blue" title="Ordem de Serviço" subtitle="Comportamento das OS">
            <SettingRow
              icon={Car}
              title="Usa veículo"
              description="Habilita o uso de veículos nas ordens de serviço"
              control={
                <Switch
                  checked={settings.os_use_vehicle}
                  onChange={(e) => updateSetting('os_use_vehicle', e.target.checked)}
                  color="primary"
                />
              }
            />
            <SettingRow
              icon={ClipboardCheck}
              title="Usa checklist de inspeção"
              description="Habilita o checklist nas ordens de serviço"
              control={
                <Switch
                  checked={settings.os_use_checklist}
                  onChange={(e) => updateSetting('os_use_checklist', e.target.checked)}
                  color="primary"
                />
              }
            />
            <SettingRow
              icon={MessageCircle}
              title="Avisar cliente ao finalizar"
              description="Envia uma mensagem por WhatsApp ao cliente quando a OS for finalizada (requer WhatsApp conectado)"
              control={
                <Switch
                  checked={Boolean(settings.os_notify_customer_on_finish)}
                  onChange={(e) => updateSetting('os_notify_customer_on_finish', e.target.checked)}
                  color="primary"
                />
              }
            />
            <SettingRow
              icon={MessageCircle}
              title="Enviar próximos serviços automaticamente"
              description="Envia por WhatsApp o lembrete dos próximos serviços aos clientes, sem ação manual (requer WhatsApp conectado)"
              control={
                <Switch
                  checked={Boolean(settings.os_auto_send_upcoming_services)}
                  onChange={(e) => updateSetting('os_auto_send_upcoming_services', e.target.checked)}
                  color="primary"
                />
              }
            />
            <SettingRow
              icon={Book}
              title="Banner de impressão"
              description="Texto exibido no topo da OS impressa"
              control={
                <Tooltip title={settings.os_banner ? 'Editar banner' : 'Configurar banner'}>
                  <IconButton
                    size="small"
                    onClick={handleOpenBannerModal}
                    sx={{ ...iconBtnSx, ...(settings.os_banner ? { color: 'success.main', borderColor: 'success.main' } : {}) }}
                    aria-label={settings.os_banner ? 'Editar banner da ordem' : 'Configurar banner da ordem'}
                  >
                    <Pencil size={14} strokeWidth={2} />
                  </IconButton>
                </Tooltip>
              }
            />
            <SettingRow
              icon={Info}
              title="Termos e condições"
              description="Exibidos no rodapé da OS impressa"
              control={
                <Tooltip title={settings.os_terms ? 'Editar termos' : 'Configurar termos'}>
                  <IconButton
                    size="small"
                    onClick={() => setTermsModalOpen(true)}
                    sx={{ ...iconBtnSx, ...(settings.os_terms ? { color: 'success.main', borderColor: 'success.main' } : {}) }}
                    aria-label={settings.os_terms ? 'Editar termos e condições' : 'Configurar termos e condições'}
                  >
                    <Pencil size={14} strokeWidth={2} />
                  </IconButton>
                </Tooltip>
              }
            />
            <SettingRow
              icon={ImageIcon}
              title="Logo da Ordem de Serviço"
              description="Exibida no cabeçalho da OS impressa"
              alignStart
              isLast
            >
              <Box
                component="label"
                sx={{
                  position: 'relative',
                  mt: 1,
                  border: '2px dashed',
                  borderColor: settings.serviceOrderLogo ? 'success.main' : 'divider',
                  borderRadius: '10px',
                  padding: '10px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  fontSize: '11.5px',
                  color: settings.serviceOrderLogo ? 'success.main' : 'text.disabled',
                  '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                }}
              >
                <Upload size={15} strokeWidth={2} />
                {settings.serviceOrderLogo ? 'Alterar logo (PNG/JPG, até 5MB)' : 'Enviar logo (PNG/JPG, até 5MB)'}
                <Box
                  component="input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload('serviceOrderLogo', e)}
                  sx={{
                    clip: 'rect(0 0 0 0)',
                    clipPath: 'inset(50%)',
                    height: 1,
                    overflow: 'hidden',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    whiteSpace: 'nowrap',
                    width: 1,
                  }}
                />
              </Box>
            </SettingRow>
          </SettingsCard>
          )}

          {/* VENDAS */}
          {currentTab === 'sales' && (
          <SettingsCard icon={ShoppingCart} tone="green" title="Vendas" subtitle="Comportamento da tela de venda (PDV)">
            <SettingRow
              icon={UserPlus}
              title="Identificar cliente na venda"
              description="Exibe a opção de identificar o cliente na tela de venda (PDV)"
              control={
                <Switch
                  checked={settings.sale_identify_customer !== false}
                  onChange={(e) => updateSetting('sale_identify_customer', e.target.checked)}
                  color="primary"
                />
              }
            />
            <SettingRow
              icon={Printer}
              title="Imprimir comprovante da venda"
              description="Oferece a impressão do comprovante ao finalizar a venda no PDV"
              isLast
              control={
                <Switch
                  checked={settings.sale_print_receipt !== false}
                  onChange={(e) => updateSetting('sale_print_receipt', e.target.checked)}
                  color="primary"
                />
              }
            />
          </SettingsCard>
          )}

          {/* GERAIS */}
          {currentTab === 'general' && (
          <SettingsCard icon={SlidersHorizontal} tone="purple" title="Gerais" subtitle="Preferências do sistema">
            <SettingRow
              icon={Package}
              title="Capacidade do estoque"
              description="Limite total de itens no estoque"
              control={
                <TextField
                  type="number"
                  size="small"
                  value={settings.maximum_stock_size}
                  onChange={(e) => updateSetting('maximum_stock_size', parseInt(e.target.value))}
                  inputProps={{ min: 1 }}
                  sx={compactNumberSx}
                />
              }
            />
            {/* `ff-hint` (prototipo-configuracoes.html #stockHint) — fora da
                coluna de texto do row, largura livre (não espremida ao lado
                do TextField), mesmo recuo do ícone (34px + 14px gap). */}
            {/* <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '-8px', mb: '6px', ml: '48px' }}>
              {stockCapacityLabel}
            </Typography> */}

            <SettingRow
              icon={Palette}
              title="Tema do sistema"
              description="Aplicado imediatamente em toda a interface"
              control={
                <Select
                  value={settings.theme}
                  onChange={(e) => applyTheme(e.target.value)}
                  size="small"
                  sx={compactSelectSx}
                >
                  <MenuItem value="light">Claro</MenuItem>
                  <MenuItem value="dark">Escuro</MenuItem>
                </Select>
              }
            />

            <SettingRow
              icon={TypeIcon}
              title="Texto em maiúsculas"
              description="Aplica maiúsculas em campos, labels e tabelas"
              isLast
              control={
                <Switch
                  checked={settings.text_uppercase || false}
                  onChange={(e) => updateSetting('text_uppercase', e.target.checked)}
                  color="primary"
                />
              }
            />
          </SettingsCard>
          )}

          {/* NOTIFICAÇÕES — avisos ao tenant por WhatsApp */}
          {currentTab === 'notifications' && (
          <SettingsCard icon={Bell} tone="blue" title="Notificações" subtitle="Escolha quais avisos receber no WhatsApp">
            <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', mb: 1.5 }}>
              Os avisos são enviados pelo WhatsApp do sistema para o celular cadastrado em Minha Conta.
              {enterpriseCellPhone !== null && !String(enterpriseCellPhone).trim() && ' Cadastre o celular em Minha Conta para receber os avisos.'}
            </Typography>
            {[
              { key: 'notify_wa_low_stock', icon: AlertTriangle, title: 'Estoque baixo', description: 'Avisa quando a quantidade de um produto atingir o estoque mínimo' },
              { key: 'notify_wa_sale_created', icon: ShoppingCart, title: 'Venda realizada', description: 'Avisa quando uma venda for concluída' },
              { key: 'notify_wa_sale_canceled', icon: XCircle, title: 'Venda estornada ou cancelada', description: 'Avisa quando uma venda for estornada ou cancelada' },
              { key: 'notify_wa_os_billed', icon: FileText, title: 'Ordem de serviço faturada', description: 'Avisa quando o pagamento de uma ordem de serviço for registrado' },
              { key: 'notify_wa_os_canceled', icon: XCircle, title: 'Ordem de serviço cancelada', description: 'Avisa quando uma ordem de serviço for cancelada' },
              { key: 'notify_wa_os_deleted', icon: Trash2, title: 'Ordem de serviço excluída', description: 'Avisa quando uma ordem de serviço for excluída' },
            ].map((item, index, list) => (
              <SettingRow
                key={item.key}
                icon={item.icon}
                title={item.title}
                description={item.description}
                isLast={index === list.length - 1}
                control={
                  <Switch
                    checked={Boolean(settings[item.key])}
                    onChange={(e) => updateSetting(item.key, e.target.checked)}
                    color="primary"
                  />
                }
              />
            ))}
          </SettingsCard>
          )}

          {/* INTEGRAÇÕES */}
          {/* Cada card só aparece quando o plano inclui o respectivo módulo */}
          {currentTab === 'integrations' && hasWhatsapp && (
          <SettingsCard icon={MessageCircle} tone="green" title="WhatsApp" subtitle="Conecte o número da sua empresa ao sistema">
            <WhatsappConnection onNotify={(message) => setSnackbar({ open: true, message, severity: 'success' })} />
          </SettingsCard>
          )}
          {currentTab === 'integrations' && hasMarketplace && (
          <SettingsCard icon={ShoppingCart} tone="green" title="Integrações" subtitle="Canais de venda externos">
            <SettingRow
              icon={ShoppingCart}
              iconBg="warning.bg"
              iconColor="warning.main"
              title="Mercado Livre"
              description="Sincronize produtos e pedidos"
              isLast
              control={<ConnBadge connected={isMercadoLivreConnected} />}
            />
            <Tooltip
              title={
                mercadoLivreAuthLoading
                  ? 'Gerando link…'
                  : mercadoLivreDisconnectLoading
                    ? 'Desconectando…'
                    : isMercadoLivreConnected
                      ? 'Desconectar da conta do Mercado Livre'
                      : 'Autenticar com o Mercado Livre'
              }
            >
              <span>
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  color={isMercadoLivreConnected ? 'error' : 'primary'}
                  onClick={isMercadoLivreConnected ? handleOpenMercadoLivreDisconnect : handleMercadoLivreAuth}
                  disabled={mercadoLivreAuthLoading || mercadoLivreDisconnectLoading}
                  startIcon={
                    mercadoLivreAuthLoading || mercadoLivreDisconnectLoading ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : isMercadoLivreConnected ? (
                      <LogOut size={14} strokeWidth={2} />
                    ) : (
                      <KeyRound size={14} strokeWidth={2} />
                    )
                  }
                  sx={{ mt: '6px', justifyContent: 'center' }}
                >
                  {isMercadoLivreConnected ? 'Desconectar' : 'Autenticar com Mercado Livre'}
                </Button>
              </span>
            </Tooltip>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', mt: '12px' }}>
              Após autenticar, o catálogo de produtos poderá ser sincronizado automaticamente com seus anúncios.
            </Typography>
          </SettingsCard>
          )}
        </Box>

        {/* Modal de Termos e Condições */}
        <DialogForm
          open={termsModalOpen}
          onClose={() => setTermsModalOpen(false)}
          title="Termos e condições"
          subtitle="Exibido no rodapé da ordem de serviço impressa"
          iconType="info"
          onSubmit={(e) => {
            e.preventDefault();
            setIsDirty(true);
            setTermsModalOpen(false);
          }}
          confirmButtonText="Salvar"
          cancelButtonText="Cancelar"
        >
          <TextAreaWithCounter
            label="Termos e Condições"
            name="serviceOrderTerms"
            maxLength={1000}
            value={settings.os_terms}
            handleChange={(e) => {
              setSettings(prev => ({
                ...prev,
                os_terms: e.target.value
              }));
            }}
            captionText="Este texto será exibido no rodapé de toda ordem de serviço impressa."
            variant="outlined"
            rows={8}
            sx={ffFieldSx}
          />

          <TextPreview value={settings.os_terms} />
        </DialogForm>

        {/* Modal para edição do banner */}
        <DialogForm
          open={bannerModalOpen}
          onClose={handleCloseBannerModal}
          title="Banner de impressão"
          subtitle="Exibido no topo de toda ordem de serviço impressa"
          iconType="info"
          onSubmit={(e) => {
            e.preventDefault();
            handleSaveBannerText();
          }}
          confirmButtonText="Salvar"
          cancelButtonText="Cancelar"
        >
          <TextAreaWithCounter
            label="Texto do Banner"
            name="serviceOrderBanner"
            maxLength={1000}
            value={bannerText}
            handleChange={(e) => setBannerText(e.target.value)}
            captionText="Destaque garantias, qualidade dos serviços ou mensagens promocionais."
            variant="outlined"
            rows={5}
            sx={ffFieldSx}
          />

          <TextPreview value={bannerText} />
        </DialogForm>

        {/* Modal de confirmação de desconexão do Mercado Livre — mesmo padrão
            de ação destrutiva do `DialogForm` usado em `pages/products/List.js` */}
        <DialogForm
          iconType="warning"
          open={mercadoLivreDisconnectDialogOpen}
          title="Desconectar Mercado Livre"
          content="Tem certeza que deseja desconectar sua conta do Mercado Livre?"
          secondaryContent="A sincronização automática de produtos e pedidos será interrompida até que você autentique novamente."
          confirmButtonText="Sim, desconectar"
          cancelButtonText="Cancelar"
          onSubmit={(e) => {
            e.preventDefault();
            handleMercadoLivreDisconnect();
          }}
          onClose={handleCloseMercadoLivreDisconnect}
          loading={mercadoLivreDisconnectLoading}
          loadingText="Desconectando..."
        />

        {/* Snackbar para feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            icon={<CheckCircle2 size={18} strokeWidth={2} />}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* savebar — DESIGN-SYSTEM.md §6.4: só aparece quando há alteração pendente */}
        <Box
          sx={{
            position: 'fixed',
            left: actionFooterLeft,
            right: 0,
            bottom: 0,
            zIndex: theme.zIndex.appBar,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -6px 20px rgba(15,23,42,.08)',
            px: 3,
            py: '14px',
            pl: 'calc(24px + env(safe-area-inset-left, 0px))',
            pr: 'calc(24px + env(safe-area-inset-right, 0px))',
            pb: 'calc(14px + env(safe-area-inset-bottom, 0px))',
            // Mobile (`.formbar`): Descartar/Salvar lado a lado, 10px 16px 12px.
            ...(isMobileShell ? { p: '10px 16px calc(12px + env(safe-area-inset-bottom, 0px))', gap: '10px', flexWrap: 'nowrap', boxShadow: '0 -8px 24px rgba(15,23,42,.05)' } : {}),
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            flexWrap: 'wrap',
            transition: 'left .2s ease, transform .25s ease',
            transform: isDirty ? 'translateY(0)' : 'translateY(100%)',
            pointerEvents: isDirty ? 'auto' : 'none',
          }}
        >
          {!isMobileShell && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '12.5px', color: 'text.secondary' }}>
            <AlertTriangle size={15} strokeWidth={2} color={theme.palette.warning.main} />
            Você tem alterações não salvas.
          </Box>}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1.25, ...(isMobileShell ? { ml: 0, flex: 1, gap: '10px' } : {}) }}>
            <Button variant="outlined" size="small" onClick={discardChanges} disabled={saving} sx={isMobileShell ? mobileBarButtonSx : undefined}>
              Descartar
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={isMobileShell ? mobileBarButtonSx : undefined}
              onClick={saveSettings}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : (isMobileShell ? undefined : <Save size={14} strokeWidth={2} />)}
            >
              {saving ? 'Salvando...' : (isMobileShell ? 'Salvar' : 'Salvar configurações')}
            </Button>
          </Box>
        </Box>
      </Box>
    </PageScaffold>
  );
}
